/**
 * スムーズスクロール（Lenis）モジュール
 * 
 * @package LIVMO
 * @version 1.0.0
 */

// CDN使用の場合: Lenisはグローバル変数として利用可能
// npm使用の場合: import Lenis from '@studio-freight/lenis'

class SmoothScroll {
  constructor() {
    this.lenis = null;
    this.rafId = null;
    this.isInitialized = false;
    this.mutationObserver = null;
    this.resizeCheckInterval = null;
    this.lastDocumentHeight = 0;
    this.tickerCallback = null; // GSAP tickerのコールバック参照を保持
  }

  /**
   * 初期化
   */
  init() {
    // GSAPとScrollTriggerが読み込まれているか確認
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      return;
    }

    // Lenisが読み込まれているか確認
    if (typeof Lenis === 'undefined') {
      return;
    }

    // prefers-reduced-motion の確認
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return;
    }

    // Lenisインスタンスの作成
    // wrapperとcontentを明示的に指定して、フッターまで正しくスクロールできるようにする
    this.lenis = new Lenis({
      duration: 1.0,
      easing: (t) => 1 - Math.pow(1 - t, 3), // ease-out-cubic（より滑らか）
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,  // タッチデバイスでは通常スクロール（パフォーマンス考慮）
      touchMultiplier: 2,
      wheelMultiplier: 0.8, // スクロール感度を少し下げてより滑らかに
      infinite: false,
      // autoRafはGSAP tickerを使う場合はfalse（手動管理）
      autoRaf: false,
      // アンカーリンクの設定（Lenisのデフォルト機能を使用）
      anchors: {
        offset: 0,
        onComplete: () => {
          // アンカーリンクスクロール完了時の処理（必要に応じて）
        }
      }
    });
    
    // 初期化時にドキュメントの高さを記録
    this.lastDocumentHeight = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
    
    // 初期化後に一度resize()を呼び出して、フッターまで正しくスクロールできるようにする
    setTimeout(() => {
      this.recalculateHeight();
    }, 100);

    // GSAP ScrollTriggerとの統合
    this.setupScrollTriggerIntegration();

    // requestAnimationFrameループの開始
    this.startRafLoop();

    // リサイズ時にLenisの高さを再計算（フッターが見切れないようにする）
    this.setupResizeHandler();

    // DOM変更の監視（フッターが見切れないようにする）
    this.setupMutationObserver();

    // 定期的な高さチェック（フッターが見切れないようにする）
    this.setupHeightCheck();

    // 画像読み込み完了の監視
    this.setupImageLoadHandler();

    // アンカーリンクのスムーズスクロール設定
    this.setupAnchorLinks();

    // グローバルに公開（他のモジュールからアクセス可能にする）
    window.lenis = this.lenis;
    window.lenis.isInitialized = true;

    this.isInitialized = true;
  }

  /**
   * GSAP ScrollTriggerとの統合（推奨設定）
   */
  setupScrollTriggerIntegration() {
    if (!this.lenis || typeof ScrollTrigger === 'undefined' || typeof gsap === 'undefined') return;

    // Lenisのスクロールイベントに合わせて全てのScrollTriggerインスタンスをアップデート
    this.lenis.on('scroll', ScrollTrigger.update);

    // LenisのrafメソッドをGSAP tickerに追加
    // tickerのタイムスタンプの単位は秒なので、ミリ秒に変換
    // コールバック参照を保持して、後で削除できるようにする
    this.tickerCallback = (time) => {
      if (this.lenis) {
        this.lenis.raf(time * 1000);
      }
    };
    gsap.ticker.add(this.tickerCallback);

    // レンダリング時にラグが発生した際のアニメーション調整機能を無効化
    gsap.ticker.lagSmoothing(0);

    // ScrollTriggerのscrollerProxyを設定
    // document.bodyを使用して、フッターまで正しくスクロールできるようにする
    ScrollTrigger.scrollerProxy(document.body, {
      scrollTop: (value) => {
        if (value !== undefined) {
          this.lenis.scrollTo(value, { immediate: true });
        }
        return this.lenis.scroll;
      },
      getBoundingClientRect: () => {
        return {
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight
        };
      },
      pinType: document.body.style.transform ? "transform" : "fixed"
    });
  }

  /**
   * requestAnimationFrameループの開始（GSAP tickerを使う場合は不要）
   * ただし、GSAPが読み込まれていない場合のフォールバックとして残す
   */
  startRafLoop() {
    // GSAP tickerが使える場合は、手動rafは不要
    if (typeof gsap !== 'undefined' && typeof gsap.ticker !== 'undefined') {
      return;
    }

    const raf = (time) => {
      if (this.lenis) {
        this.lenis.raf(time);
      }
      this.rafId = requestAnimationFrame(raf);
    };
    this.rafId = requestAnimationFrame(raf);
  }

  /**
   * Lenisの高さを再計算する（フッターが見切れないようにする）
   */
  recalculateHeight() {
    if (!this.lenis) return;

    // 現在のドキュメントの高さを取得
    const currentHeight = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );

    // 高さが変更されていた場合のみresize()を呼び出す
    if (currentHeight !== this.lastDocumentHeight) {
      this.lastDocumentHeight = currentHeight;
      this.lenis.resize();
      
      // ScrollTriggerも更新
      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.refresh();
      }
    }
  }

  /**
   * リサイズハンドラーの設定（フッターが見切れないようにする）
   */
  setupResizeHandler() {
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.recalculateHeight();
      }, 100);
    };
    
    window.addEventListener('resize', handleResize);
    
    // ページ読み込み完了後にも一度実行
    if (document.readyState === 'complete') {
      handleResize();
    } else {
      window.addEventListener('load', () => {
        handleResize();
        // loadイベント後にも少し遅延させて再計算
        setTimeout(() => {
          this.recalculateHeight();
        }, 300);
      });
    }
    
    // 初期化後にも複数回実行（フッターまで正しくスクロールできるようにする）
    setTimeout(() => {
      this.recalculateHeight();
    }, 200);
    
    setTimeout(() => {
      this.recalculateHeight();
    }, 500);
    
    setTimeout(() => {
      this.recalculateHeight();
    }, 1000);
  }

  /**
   * MutationObserverの設定（DOM変更を監視して高さを再計算）
   */
  setupMutationObserver() {
    if (typeof MutationObserver === 'undefined') return;

    this.mutationObserver = new MutationObserver(() => {
      // DOM変更があったら少し遅延させて高さを再計算
      setTimeout(() => {
        this.recalculateHeight();
      }, 100);
    });

    // bodyとその子要素の変更を監視
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false
    });
  }

  /**
   * 定期的な高さチェック（フッターが見切れないようにする）
   */
  setupHeightCheck() {
    // 5秒ごとに高さをチェック
    this.resizeCheckInterval = setInterval(() => {
      this.recalculateHeight();
    }, 5000);
  }

  /**
   * 画像読み込み完了の監視
   */
  setupImageLoadHandler() {
    // 既存の画像の読み込み完了を待つ
    const images = document.querySelectorAll('img');
    let loadedCount = 0;
    const totalImages = images.length;

    if (totalImages === 0) {
      // 画像がない場合は即座に再計算
      setTimeout(() => {
        this.recalculateHeight();
      }, 300);
      return;
    }

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        // すべての画像が読み込まれたら高さを再計算
        setTimeout(() => {
          this.recalculateHeight();
        }, 300);
      }
    };

    images.forEach((img) => {
      if (img.complete) {
        checkAllLoaded();
      } else {
        img.addEventListener('load', checkAllLoaded, { once: true });
        img.addEventListener('error', checkAllLoaded, { once: true });
      }
    });

    // タイムアウト設定（10秒経過したら強制的に再計算）
    setTimeout(() => {
      this.recalculateHeight();
    }, 10000);
  }

  /**
   * アンカーリンクのスムーズスクロール設定
   * Lenisのanchorsオプションで自動処理されるため、手動処理は不要
   * カスタム処理が必要な場合のみ手動でイベントリスナーを追加
   */
  setupAnchorLinks() {
    if (!this.lenis) return;

    // Lenisのanchorsオプションで自動処理されるため、基本的には不要
    // カスタム処理が必要な場合のみ以下を有効化
    /*
    const anchors = document.querySelectorAll('a[href^="#"]');
    
    anchors.forEach(anchor => {
      anchor.removeEventListener('click', this.handleAnchorClick);
      anchor.addEventListener('click', this.handleAnchorClick.bind(this), { passive: false });
    });
    */
  }

  /**
   * アンカーリンククリックハンドラー（カスタム処理が必要な場合のみ使用）
   */
  handleAnchorClick(e) {
    if (!this.lenis) return;

    const href = e.currentTarget.getAttribute('href');
    if (!href || href === '#') return;

    const target = document.querySelector(href);
    if (!target) return;

    e.preventDefault();
    
    this.lenis.scrollTo(target, {
      offset: 0,
      duration: 1.5,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t))
    });
  }

  /**
   * 破棄（ページ遷移時など）
   */
  destroy() {
    // GSAP tickerから削除
    if (typeof gsap !== 'undefined' && typeof gsap.ticker !== 'undefined' && this.tickerCallback) {
      gsap.ticker.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    if (this.resizeCheckInterval) {
      clearInterval(this.resizeCheckInterval);
      this.resizeCheckInterval = null;
    }

    if (this.lenis) {
      this.lenis.destroy();
      this.lenis = null;
    }

    window.lenis = null;
    this.isInitialized = false;
    this.lastDocumentHeight = 0;
  }

  /**
   * Lenisインスタンスを取得
   */
  getLenis() {
    return this.lenis;
  }

  /**
   * スクロールを停止
   */
  stop() {
    if (this.lenis) {
      this.lenis.stop();
    }
  }

  /**
   * スクロールを再開
   */
  start() {
    if (this.lenis) {
      this.lenis.start();
    }
  }
}

  /**
   * 初期化関数
   */
  function initSmoothScroll() {
    // 既に初期化されている場合はスキップ
    if (window.__smoothScrollInstance) {
      return window.__smoothScrollInstance;
    }

    const smoothScroll = new SmoothScroll();
  
    // LenisとGSAPが読み込まれるのを待つ
    function tryInit() {
      if (typeof Lenis !== 'undefined' && typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        // DOMContentLoadedで初期化
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            smoothScroll.init();
          });
        } else {
          // 既にDOMContentLoadedが発火している場合は即座に初期化
          smoothScroll.init();
        }
        window.__smoothScrollInstance = smoothScroll;
      } else {
        // まだ読み込まれていない場合は少し待って再試行
        setTimeout(tryInit, 50);
      }
    }
    
    tryInit();
    return smoothScroll;
  }

// 自動初期化
initSmoothScroll();

export default SmoothScroll;

